// src/lib/importJobs.ts
//
// React-side view of /api/imports/jobs/active + job-detail polling.
// Only used by staff (the endpoint requires staff auth), but the
// hook is no-op when isAuthenticated is false.
//
// useJobTracker() returns:
//   - activeJobs: the list of currently running jobs
//   - watch(jobId): poll a specific job until it lands in done/error
//   - cancel(jobId): ask the backend to cancel
//
// The header imports this to show a single, compact progress chip
// (one job at a time; multiple in queue is collapsed into a counter).

import { useCallback, useEffect, useRef, useState } from "react";

export type ImportJobKind = "preview" | "apply" | "rollback";

export interface ImportJob {
  job_id: string;
  kind: ImportJobKind;
  batch_id: string | null;
  actor: string;
  started_at: number;
  finished_at: number | null;
  phase: "running" | "done" | "error";
  progress: number;
  message: string;
  has_result: boolean;
  error: string | null;
}

export function useImportJobs(enabled: boolean) {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await fetch("/api/imports/jobs/active", {
        credentials: "same-origin",
      });
      if (!alive.current) return;
      if (!r.ok) {
        setJobs([]);
        return;
      }
      const data = (await r.json()) as { jobs: ImportJob[] };
      setJobs(data.jobs);
    } catch {
      if (!alive.current) return;
    }
  }, [enabled]);

  const watch = useCallback(async (jobId: string): Promise<ImportJob> => {
    const tick = async (): Promise<ImportJob> => {
      const r = await fetch(`/api/imports/jobs/${jobId}`, {
        credentials: "same-origin",
      });
      if (!r.ok) {
        throw new Error(`job ${jobId} not found (HTTP ${r.status})`);
      }
      return (await r.json()) as ImportJob;
    };
    let last = await tick();
    while (last.phase === "running") {
      await new Promise((res) => setTimeout(res, 500));
      last = await tick();
    }
    return last;
  }, []);

  const cancel = useCallback(async (jobId: string): Promise<boolean> => {
    const r = await fetch(`/api/imports/jobs/${jobId}/cancel`, {
      method: "POST", credentials: "same-origin",
    });
    return r.ok;
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!enabled) { setJobs([]); return; }
    void refresh();
    const t = setInterval(() => void refresh(), 1_000);
    return () => { alive.current = false; clearInterval(t); };
  }, [enabled, refresh]);

  return { jobs, refresh, watch, cancel };
}
